import { useTranslation } from 'react-i18next'
import { useIsSmallScreenFalse } from 'packages/core'
import { Box, styled } from '@mui/material'
import { useApplicationSecrets } from 'hooks'
import { Filters, Button, DeleteCredential, CardViewLoader } from 'components'
import SecretCard from './SecretCard'
import { usePageContext } from 'pageContext'
import useSubcomponents from 'useSubcomponents'
import { CreateWorkflowButton } from '../CreateWorkflowButton'
import { useHistory } from 'react-router'
import EidTableGrid from 'components/EidTableGrid/EidTableGrid'
import {
    ListTable,
    ListTableHead,
    ListTableRow,
} from 'components/EidTableGrid/ListView'
import { UIActionsGearButton } from 'components/UIActions/UIActionsGearButton'

const TableOverView = styled(Box)({
    padding: '16px 0px',
    '& table': {
        boxShadow: '0 4px 17px 0 rgba(0, 0, 0, 0.07)',
    },
})

const ApplicationSecrets = ({ azureApplicationId, resourceId }) => {
    const { t } = useTranslation()
    const { canAddClientSecret, getAccessBasedAttributes } = useSubcomponents()
    const history = useHistory()
    const isSmallScreen = useIsSmallScreenFalse()

    const {
        list,
        loading: isLoading,
        loadingMore: isLoadingMore,
        pagination,
    } = useApplicationSecrets(azureApplicationId)

    const [state] = usePageContext()

    const masterPasswordLockStyle = {
        height: '100%',
        width: state.masterPasswordWidth,
        left: state.masterPasswordLeftWidth,
    }

    const attributes = [
        {
            name: 'friendlyName',
            sortable: true,
            label: 'FriendlyName',
            isHeader: true,
            showInCard: false,

            headerCellStyles: {
                width: '150px',
                minWidth: '150px',
            },
            styles: {
                width: '150px',
                minWidth: '150px',
                fontWeight: 'bold',
            },
            //NOTE : Keeping the commented out code for subcomponent traceability
            // requireAccess: {
            //     control: 'ResourceAdmin-Application-Secret-List-FriendlyName',
            // },
        },
        {
            name: 'ownerFriendlyName',
            sortable: true,
            hideValue: true,
            label: 'Owner',
            component: {
                componentName: 'ApplicationOwner',
            },
            headerCellStyles: {
                width: '160px',
            },
            styles: {
                width: '160px',
            },
            requireAccess: {
                control: 'ResourceAdmin-Application-Secret-List-Owner',
            },
        },
        {
            name: 'status',
            label: 'Status',
            hideValue: true,
            showMasterLock: 'true',
            component: {
                componentName: 'CredentialStatusButton',
            },
            styles: {
                width: '150px',
                overflow: 'visible',
            },

            headerCellStyles: {
                width: '150px',
            },
            requireAccess: {
                control: 'ResourceAdmin-Application-Secret-List-Status',
            },
        },
        {
            name: 'password',
            label: 'Password',
            hideValue: true,
            showMasterLock: true,
            component: {
                componentName: 'CredentialLockedBox',
            },
            headerCellStyles: {
                width: '120px',
            },
            styles: {
                width: '120px',
                padding: '0px',
            },
            requireAccess: {
                control: 'ResourceAdmin-Application-Secret-List-Password',
            },
        },
        {
            name: 'endDateTimeUtc',
            hideValue: true,
            sortable: true,
            label: 'RemainingTime',
            component: {
                componentName: 'CredentialRemainingTime',
            },
            headerCellStyles: {
                width: '170px',
                minWidth: '170px',
                maxWidth: '170px',
            },
            styles: {
                width: '170px',
                minWidth: '170px',
                maxWidth: '170px',
            },
            requireAccess: {
                control: 'ResourceAdmin-Application-Secret-List-RemainingTime',
            },
        },
        {
            hideValue: true,
            showInCard: false,
            requireAccess: {
                control: 'ResourceAdmin-Application-Secret-List-Actions',
            },
            component: {
                componentName: 'renderer',
                render: (item) => {
                    return (
                        <Box
                            style={{
                                display: 'flex',
                                justifyContent: 'end',
                            }}
                        >
                            <Button.Details
                                item={item}
                                onClick={() => {
                                    history.push(
                                        `${history.location.search}&secretId=${item?.id}`,
                                    )
                                }}
                            />
                            <span style={{ marginRight: '10px' }} />
                            <DeleteCredential item={item} />
                            <span style={{ marginRight: '10px' }} />
                            <UIActionsGearButton item={item} noun='ResourceAdminSecrets' />
                        </Box>
                    )
                },
            },
        },
    ]

    return (
        <Box>
            <Box
                width="100%"
                display="flex"
                alignItems="space-between"
                justifyContent="space-between"
            >
                {canAddClientSecret && (
                    <Box data-protectedsubcomponent={canAddClientSecret}>
                        <CreateWorkflowButton
                            label={t('AddClientSecret')}
                            workflowName="CreateAzureAppClientSecret"
                            resourceId={resourceId}
                        />
                    </Box>
                )}
                <Box
                    style={{
                        margin: isSmallScreen ? '16px' : '0px 0px 0px auto',
                        width: isSmallScreen ? 'auto' : '308px',
                    }}
                >
                    <Filters.TextSearch outlined />
                </Box>
            </Box>
            <TableOverView>
                <EidTableGrid
                    noDataMessage={t('NoDataMessage')}
                    pagination={pagination}
                    isLoading={isLoading}
                    isLoadingMore={isLoadingMore}
                    attributes={getAccessBasedAttributes(attributes)}
                    list={list}
                    tableViewConfig={{
                        TableComponent: ListTable,
                        TableHeadComponent: ListTableHead,
                        TableRowComponent: ListTableRow,
                        showMasterPasswordLock: true,
                        masterPasswordLockStyle,
                    }}
                    cardViewConfig={{
                        LoadingIndicatorComponent: CardViewLoader,
                        CardComponent: SecretCard,
                    }}
                />
            </TableOverView>
        </Box>
    )
}

export default ApplicationSecrets
